import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nc_cart") || "[]"); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("nc_cart", JSON.stringify(items));
  }, [items]);

  // Refresh cart prices/details from the backend on load so the cart can never show a stale
  // (localStorage) price. Bump items are re-derived to 20% off the current guide price.
  useEffect(() => {
    api.get("/products").then((r) => {
      const bySlug = Object.fromEntries((r.data || []).map((p) => [p.slug, p]));
      setItems((prev) => prev.map((it) => {
        const p = bySlug[it.slug];
        if (!p) return it;
        const price = it.bump ? Math.round(p.price * 0.8 * 100) / 100 : p.price;
        return { ...it, price, name: p.name, image: p.image, type: p.type, category: p.category };
      }));
    }).catch(() => {});
  }, []);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found) return prev.map((i) => i.slug === product.slug ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, {
        slug: product.slug, name: product.name, price: product.price,
        image: product.image, type: product.type, category: product.category, quantity: qty,
      }];
    });
    setOpen(true);
  };

  // one-click order bump: add a digital guide at 20% off (stacks with GROW10)
  const addBump = (guide) => {
    setItems((prev) => {
      if (prev.find((i) => i.slug === guide.slug)) return prev;
      return [...prev, {
        slug: guide.slug, name: guide.name, price: Math.round(guide.price * 0.8 * 100) / 100,
        image: guide.image, type: "digital", category: "Ebooks & Guides", quantity: 1, bump: true,
      }];
    });
  };

  const setQty = (slug, qty) => {
    if (qty <= 0) return remove(slug);
    setItems((prev) => prev.map((i) => i.slug === slug ? { ...i, quantity: qty } : i));
  };
  const remove = (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug));
  const clear = () => setItems([]);

  const count = items.reduce((a, i) => a + i.quantity, 0);
  const subtotal = items.reduce((a, i) => a + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, addBump, setQty, remove, clear, count, subtotal, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}
